/** The trait table (04 §4.3): what a recruit arrives with, and what a career leaves behind. */

import type { TraitId } from '../types';
import type { ModEffect } from './perks';

export interface TraitInfo {
  id: TraitId;
  name: string;
  text: string;
  /** Recruits are generated with one of these. */
  recruit: boolean;
  effect: ModEffect;
}

export const TRAITS: readonly TraitInfo[] = [
  { id: 'farm-strong', recruit: true, name: 'Farm Strong', text: '+3 carry units.',
    effect: { add: { carryBonus: 3 } } },
  // R6: `staminaPool` is a run-endurance divisor, not a pool - see Iron Lungs in perks.ts.
  { id: 'track-star', recruit: true, name: 'Track Star', text: '+8 % run speed; winded 10 % sooner.',
    effect: { mul: { runSpeed: 1.08, staminaPool: 0.9 } } },
  { id: 'hunter', recruit: true, name: 'Hunter', text: '+10 % carbine range, crawl speed -10 %.',
    effect: { mul: { carbineRange: 1.1, crawlSpeed: 0.9 } } },
  { id: 'swimmer', recruit: true, name: 'Swimmer', text: 'Wading costs no extra stamina.',
    effect: { mul: { wadeFatigue: 0 } } },
  { id: 'lucky', recruit: true, name: 'Lucky',
    text: 'Once per operation, the wound that would have downed this ranger is ignored.', effect: {} },
  { id: 'hothead', recruit: true, name: 'Hothead', text: '+10 % fire rate; aim spread +10 % when enemies are alerted.',
    effect: { mul: { fireRate: 1.1, alertedSpread: 1.1 } } },
  // R5: `aimSpreadStill`, so it bit only while he stood still - a green recruit shot like a veteran
  // the moment he started walking. `aimSpread` is the nerves he carries in every stance (ROUND5 §3.6).
  // R6: "First mission only" was the half that was not true - `roster.ts` sheds Green on an
  // EXTRACTION, so a recruit who walked out, or was taken, carries it into the next one. The
  // second sentence was always the rule, so it is now the whole of it.
  { id: 'green', recruit: true, name: 'Green', text: 'Aim spread +15 %. Gone after one extraction.',
    effect: { mul: { aimSpread: 1.15 } } },
  { id: 'old-wound', recruit: false, name: 'Old Wound', text: 'Winded 10 % sooner at a run, permanent.',
    effect: { mul: { staminaPool: 0.9 } } },
  { id: 'shaken', recruit: false, name: 'Shaken', text: 'Aim spread +15 % for the next 2 missions.',
    effect: { mul: { aimSpread: 1.15 } } },
  { id: 'decorated', recruit: false, name: 'Decorated',
    text: 'Other rangers gain +5 % merit while this ranger is alive in the company.', effect: {} },
  // Names the Options setting it rides on: the pips are an aid a player turns on, and with them off there is
  // no hearing to take 30 % of. A trait that promises a permanent effect has to say where to see it.
  { id: 'tinnitus', recruit: false, name: 'Tinnitus',
    text: 'VISUALISE SOUNDS pips reach 30 % less far, permanent.',
    effect: { mul: { hearingRadius: 0.7 } } },
  { id: 'blooded', recruit: false, name: 'Blooded', text: 'Shrugs off Green-type penalties; immune to Shaken.',
    effect: {} },
];

export const RECRUIT_TRAITS: readonly TraitId[] = TRAITS.filter((t) => t.recruit).map((t) => t.id);

export const TRAIT_RULES = {
  /** Missions a rescued POW stays shaken. */
  shakenMissions: 2,
  /** Missions survived that make a ranger Blooded. */
  bloodedAfter: 5,
  /** Chance of an Old Wound after a critical wound (04 §4.4). */
  oldWoundChance: 0.35,
  /** Merit bonus to the rest of the company while a Decorated ranger lives. */
  decoratedMeritBonus: 0.05,
  /** Traits whose penalties Blooded cancels. */
  bloodedCancels: ['green', 'shaken'] as readonly TraitId[],
} as const;
